import {
  XYPlot,
  XAxis,
  YAxis,
  VerticalBarSeries,
  HorizontalGridLines
} from "react-vis";
import { useState , useMemo } from "react";
import { Hint } from "react-vis";
import "react-vis/dist/style.css";
import { FormControlLabel, Switch, Box } from "@material-ui/core"
import useStyles from './style'
import { withStyles } from "@material-ui/core/styles"

export default function NetCapacityChart({ data }) {
  const [showBattery, setShowBattery] = useState(true)
  const [showGridExport, setShowGridExport] = useState(true)
  const [showEvse, setShowEvse] = useState(true)
  const [showWaterHeater, setShowWaterHeater] = useState(true)
  const [showHeatPump, setShowHeatPump] = useState(true)
  const [hoveredPoint, setHoveredPoint] = useState(null)
  const classes = useStyles()

  const ColoredSwitch = withStyles({
    switchBase: {
      color: props => props.switchcolor,
      '&$checked': {
        color: props => props.switchcolor,
      },
      '&$checked + $track': {
        backgroundColor: props => props.switchcolor,
        opacity: 1,
      },
    },
    track: {
      backgroundColor: props => props.switchcolor,
    },
    checked: {
      color: props => props.switchcolor,
    },
  })(Switch);
  
  const Toggle = ({ label, checked, onChange, color }) => {
    return (
      <Box className={classes.switchWrapper}>
        <FormControlLabel
          control={
            <ColoredSwitch
              checked={checked}
              onChange={onChange}
              switchcolor={color}
            />
          }
          label={label}
          labelPlacement="right"
        />
      </Box>
    )
  }

  const { yDomainMin, yDomainMax } = useMemo(() => {
    let min = 0;
    let max = 0;

    data.forEach(d => {
      let pos = 0;
      let neg = 0;

      const add = (value, forceNegative = false) => {
        if (!Number.isFinite(value)) return;
        if (!forceNegative) pos += value;
        else neg -= value;
      };

      if (showBattery) add(d.value.available_battery_capacity_mw);
      if (showGridExport) add(d.value.grid_export);
      if (showEvse) add(d.value.evse, true);
      if (showWaterHeater) add(d.value.water_heater, true);
      if (showHeatPump) add(d.value.heat_pump, true);

      max = Math.max(max, pos);
      min = Math.min(min, neg);
    });

    return { yDomainMin: min, yDomainMax: max };
  }, [
    data,
    showBattery,
    showGridExport,
    showEvse,
    showWaterHeater,
    showHeatPump
  ]);

const valueMap = useMemo(() => {
  const map = {};
  data.forEach((d, index) => {
    map[index] = {
      time: d.time,
      battery: d.value.available_battery_capacity_mw,
      gridExport: d.value.grid_export,
      evse: d.value.evse,
      waterHeater: d.value.water_heater,
      heatPump: d.value.heat_pump
    };
  });
  return map;
}, [data]);

const stackedData = useMemo(() => {
  return data.map((d,index) => {
    let posBase = 0;
    let negBase = 0;

    const bars = {};

    const addBar = (key, value, forceNegative = false) => {    
      if(forceNegative) {
        bars[key] = {
          x : index,
          y0 : negBase - value,
          y : negBase
        };
        negBase -= value;
      } else {
        bars[key] = {
          x : index,
          y0 : posBase,
          y : posBase + value
        };
        posBase += value;
      }
    }

    if (showBattery) {
      addBar("batteryBar", d.value.available_battery_capacity_mw);
    }

    if (showGridExport) {
      addBar("gridExportBar", d.value.grid_export);
    }

    if (showEvse) {
      addBar("evseBar", d.value.evse, true);
    }
    if (showWaterHeater) {
      addBar("waterHeaterBar", d.value.water_heater, true);
    }
    if (showHeatPump) {
      addBar("heatPumpBar", d.value.heat_pump, true);
    }

    return bars;
  });
}, [data, showBattery, showGridExport,  showEvse, showWaterHeater, showHeatPump]);


const yDomain = useMemo(() => {
  if (!Number.isFinite(yDomainMin) || !Number.isFinite(yDomainMax)) {
    return [-10, 10];
  }

  const min = yDomainMin * 2;
  const max = yDomainMax * 2;

  return [Math.min(min, max), Math.max(min, max)];
}, [yDomainMin, yDomainMax])

const hourlyTicks = useMemo(() => {
  return data
    .map((d, i) => (i % 4 === 0 ? i : null))
    .filter(v => v !== null);
}, [data])

return (
  <Box>
    <Box className={classes.toggleWrapper} >
      <Toggle label="Available Solar Power" checked={showGridExport} color="#6BAED6" onChange={() => setShowGridExport(v => !v)} />
      <Toggle label="Available Storage Power" checked={showBattery} color="#00C853" onChange={() => setShowBattery(v => !v)} />
      <Toggle label="EVSE Consumption" checked={showEvse} color="#660033" onChange={() => setShowEvse(v => !v)} />
      <Toggle label="Water Heater Consumption" checked={showWaterHeater} color="#FB8C00" onChange={() => setShowWaterHeater(v => !v)} />
      <Toggle label="Heat Pump Consumption" checked={showHeatPump} color="#000080" onChange={() => setShowHeatPump(v => !v)} />
    </Box>
    <XYPlot
      xType="linear"
      width={1200}
      height={400}
      margin={{ left: 70, bottom: 90 }}
      yDomain={yDomain}
    >
      <HorizontalGridLines />

      <XAxis
        title="Time(PST)"
        style={{
          title: { fontSize: 14, fontWeight: 1000, transform: 'translate(550px, 6px)' },
          line: { stroke: '#ccc' }
        }}
        tickValues={hourlyTicks}
        tickFormat={i => data[i]?.time}
        tickLabelAngle={-90}
      />

      <YAxis 
        title="Power (MW)" 
        style={{
        title: { fontSize: 14, fontWeight: 1000 },
        line: { stroke: '#ccc' }
      }}/>

      {showBattery && (
        <VerticalBarSeries
          data={stackedData.map(d => d.batteryBar).filter(Boolean)}
          color="#00C853"
          cluster="no"
          onValueMouseOver={v =>
            setHoveredPoint({ ...v, type: "battery" })
          }
          onValueMouseOut={() => setHoveredPoint(null)}
        />
      )}

      {showGridExport && (
        <VerticalBarSeries
          data={stackedData.map(d => d.gridExportBar).filter(Boolean)}
          color="#6BAED6"
          cluster="no"
          onValueMouseOver={v =>
            setHoveredPoint({ ...v, type: "gridExport" })
          }
          onValueMouseOut={() => setHoveredPoint(null)}
        />
      )}

      {showEvse && (
        <VerticalBarSeries
          data={stackedData.map(d => d.evseBar).filter(Boolean)}
          color="#660033"
          cluster="no"
          onValueMouseOver={v => setHoveredPoint({ ...v, type: "evse" })}
          onValueMouseOut={() => setHoveredPoint(null)}
        />
      )}

      {showWaterHeater && (
        <VerticalBarSeries
          data={stackedData.map(d => d.waterHeaterBar).filter(Boolean)}
          color="#FB8C00"
          cluster="no"
          onValueMouseOver={v => setHoveredPoint({ ...v, type: "waterHeater" })}
          onValueMouseOut={() => setHoveredPoint(null)}
        />
      )}

      {showHeatPump && (
        <VerticalBarSeries
          data={stackedData.map(d => d.heatPumpBar).filter(Boolean)}
          color="#000080"
          cluster="no"
          onValueMouseOver={v => setHoveredPoint({ ...v, type: "heatPump" })}
          onValueMouseOut={() => setHoveredPoint(null)}
        />
      )}

      {hoveredPoint && (
        <Hint value={hoveredPoint}>
          <Box className={classes.hintWrapper} >
            <div style={{ fontWeight: 600, marginBottom: 4 }}>
              {valueMap[hoveredPoint.x]?.time}
            </div>
            
            {showBattery && (
              <div style={{ color: "#00C853" }}>
                ● Available Storage Power:{" "}
                {valueMap[hoveredPoint.x]?.battery !== undefined
                  ? Number(valueMap[hoveredPoint.x].battery).toFixed(4)
                  : "--"}{" "}
                MW
              </div>
            )}
            {showGridExport && (
              <div style={{ color: "#6BAED6" }}>
                ● Available Solar Power:{" "}
                {valueMap[hoveredPoint.x]?.gridExport !== undefined
                  ? Number(valueMap[hoveredPoint.x].gridExport).toFixed(4)
                  : "--"}{" "}
                MW
              </div>
            )}
            {showEvse && (
              <div style={{ color: "#660033" }}>
                ● EVSE Consumption: {valueMap[hoveredPoint.x]?.evse?.toFixed(4)} MW
              </div>
            )}

            {showWaterHeater && (
              <div style={{ color: "#FB8C00" }}>
                ● Water Heater Consumption: {valueMap[hoveredPoint.x]?.waterHeater?.toFixed(4)} MW
              </div>
            )}

            {showHeatPump && (
              <div style={{ color: "#000080" }}>
                ● Heat Pump Consumption: {valueMap[hoveredPoint.x]?.heatPump?.toFixed(4)} MW
              </div>
            )}
          </Box>
        </Hint>
      )}
    </XYPlot>
  </Box>
  );
}


